// Copyright (c) 2026 Celadonsoft
// All rights reserved.

import { mapGetters } from 'vuex';

import {
  buildAttractionListItem,
  buildCoolpassBenefit,
  buildCoolpassEntity,
  buildCoolpassOrganization,
  canAcceptBenefit,
  coolpassEntityId,
  isUsableSlug,
  withPageNode,
} from '@utils/structured-data';

import {
  BREADCRUMB_ROOT_NAME,
  SECTION_PAGE_MODULES,
  buildBreadcrumbList,
  sectionLabelFrom,
} from '@utils/breadcrumbs';

// A description taken from a rich-text body still holds entities after the
// tags are stripped, and vue-meta escapes again - `&amp;` became `&amp;amp;`.
// The textarea never enters the document, so nothing here can execute.
const decodeHtmlEntities = (text) => {
  if (!text || text.indexOf('&') === -1) return text;

  const el = document.createElement('textarea');
  el.innerHTML = text;

  return el.value;
};

// `breadcrumb` is defined on WebPage alone - narrower than `about`. The
// BreadcrumbList is a standalone graph node either way; this only controls
// whether the page node links to it.
// `geo` is defined on Place alone, and `address` on Place/Organization/Person -
// neither is a Thing-level property. A sales point whose CMS markup field is
// empty falls back to WebPage, and a street address or a pair of coordinates
// hung off that node is invalid. Same class of mistake as putting `inLanguage`
// on a Place: harmless in that a reader ignores the property, but there is no
// reason to state it.
const PLACE_TYPES = [
  'Place',
  'TouristAttraction',
  'Museum',
  'Zoo',
  'Aquarium',
  'AmusementPark',
  'LandmarksOrHistoricalBuildings',
  'PlaceOfWorship',
  'Church',
  'Castle',
  'PerformingArtsTheater',
  'MovieTheater',
  'Park',
];

const WEB_PAGE_TYPES = ['WebPage', 'CollectionPage', 'ContactPage', 'FAQPage', 'ReviewPage'];

// `about` is defined on CreativeWork only - a Place or TouristAttraction node
// is tied to the pass by the shared entity beside it in the @graph instead.
const CREATIVE_WORK_PAGE_TYPES = [
  'WebPage',
  'CollectionPage',
  'Article',
  'FAQPage',
  'ContactPage',
  'ReviewPage',
];

export default {
  computed: {
    ...mapGetters('translations', ['currentLanguage', 'languagesList']),

    baseUrl() {
      const url = process.env.VUE_APP_BASE_URL || 'https://praguecoolpass.com';
      return url.replace(/\/$/, '');
    },

    routeBaseName() {
      const name = this.$route.name;
      if (!name) return '';
      return name.split('__')[0];
    },

    // The entity's own name, without SEO decoration. Shared by the JSON-LD
    // `name` and the breadcrumb leaf so the two cannot disagree.
    // The page's own canonical URL, exposed so a page contributing product
    // facts links its offers to the language version it is actually on. Same
    // expression page() used inline, and reactive for the same reason: it is
    // $route.fullPath that triggers re-evaluation, not window.location.
    seoCanonicalUrl() {
      return window.location.origin + this.$route.fullPath;
    },

    // Hubs whose preview block this page is currently showing.
    //
    // The home page surfaces a themes and an areas preview through shared
    // blocks the CMS switches per page, and each preview is a slice - eight of
    // thirteen themes, eight of seventeen areas. A list of those eight would
    // break the rule that only a complete set may be stated, and would claim
    // the pass covers less than it does, which is the opposite of what the
    // brief asked for ("make the breadth of the offer explicit").
    //
    // The complete lists already exist, on the hubs themselves. So the preview
    // is expressed as what it actually is: a pointer. `mentions` is defined on
    // CreativeWork and takes any Thing, and unlike `mainEntity` it takes
    // several - which matters, because a page can show both previews at once.
    //
    // Read from the normalized block list - the CMS visibility flags resolved
    // into render order, not a check that anything was actually produced. A
    // block switched on with nothing to show still leaves the link, which is
    // harmless: it points at the hub, which exists regardless of the preview.
    // Note this is a weaker guarantee than `describesProduct` below, which does
    // look at the values a render produced.
    seoSharedHubMentions() {
      const blocks = Array.isArray(this.sortedSharedBlocks) ? this.sortedSharedBlocks : [];
      const origin = window.location.origin;
      const lang = this.currentLanguage;

      const hubs = {
        SharedCategories: 'categories',
        SharedAreas: 'areas',
      };

      return blocks
        .map((block) => hubs[block.component])
        .filter(Boolean)
        .map((systemKey) => this.seoRouteUrl(systemKey, lang, origin))
        .filter(Boolean);
    },

    seoEntityTitle() {
      const entity = this.seoEntity || {};
      const content = entity.content || {};
      const seo = entity.seo || {};
      const lang = this.currentLanguage;

      return (
        content?.[lang]?.title || content?.en?.title || seo.h1Title?.[lang] || seo.h1Title?.en || ''
      );
    },

    seoLeafName() {
      return this.seoEntityTitle;
    },

    // Read entirely from route meta, so a section added in the CMS gets
    // breadcrumbs with no code change. The home page gets an empty trail: a
    // one-item breadcrumb pointing at the current page says nothing.
    seoBreadcrumbTrail() {
      const route = this.$route;
      const meta = route?.meta || {};
      const lang = this.currentLanguage;
      const systemKey = meta.systemKey;

      if (!systemKey || systemKey === 'home' || systemKey === 'notFound') return [];

      const origin = window.location.origin;
      const trail = [{ name: BREADCRUMB_ROOT_NAME, url: this.seoRouteUrl('home', lang, origin) }];

      // A list or static page IS the second level and adds nothing here.
      if (meta.type === 'detail') {
        const sectionName = this.seoSectionLabel(systemKey, lang);
        const sectionUrl = this.seoRouteUrl(systemKey, lang, origin);

        // Needs both a name and a link, or it breaks the chain it expresses.
        if (sectionName && sectionUrl) {
          trail.push({ name: sectionName, url: sectionUrl });
        }
      }

      // The leaf carries no url: it is the page being viewed.
      const leafName = this.seoLeafName;
      if (!leafName) return [];

      trail.push({ name: leafName });

      return trail;
    },
  },

  watch: {
    // Published so the shared layout renders the very same array.
    seoBreadcrumbTrail: {
      immediate: true,
      handler(trail) {
        this.$store.commit('breadcrumbs/SET_TRAIL', trail);
      },
    },
  },

  methods: {
    // Router-resolved, so it matches what the navbar and cards link to.
    seoRouteUrl(systemKey, lang, origin) {
      try {
        const resolved = this.$router.resolve({
          name: `${systemKey}__${lang}`,
          params: { lang },
        });

        return resolved?.href ? `${origin}${resolved.href}` : '';
      } catch (e) {
        return '';
      }
    },

    // Read from the section's own list-page record, so the crumb and the page
    // it links to agree. Menu label as fallback (today only `news`).
    seoSectionLabel(systemKey, lang) {
      const moduleName = SECTION_PAGE_MODULES[systemKey];

      if (moduleName) {
        const pageData = this.$store.getters[`${moduleName}/pageData`];
        const label = sectionLabelFrom(pageData, lang);
        if (label) return label;
      }

      const menuData = this.$store.getters['menu/menuData'] || [];
      const item = menuData.find((entry) => entry?.resolvedLink?.systemKey === systemKey);
      const content = item?.content || {};

      // Spelled out rather than using the Translatable mixin, so this mixin
      // carries no hidden dependency on another one.
      return content[lang]?.title || content.en?.title || '';
    },

    // Same slug order the attraction card uses for the link it renders, and
    // that SitemapService.ResolveDetailSlug uses on the backend: this language
    // -> English -> legacy slug. An ObjectId counts as no slug, and '' makes
    // the list item keep its name but drop the url.
    seoAttractionUrl(attraction, lang, origin, detailKey) {
      const slugs = attraction.slugs || attraction.urlSlug || {};

      const targetSlug = [slugs[lang], slugs.en, attraction.slug].find(isUsableSlug);

      if (!targetSlug) return '';

      try {
        const resolved = this.$router.resolve({
          name: `${detailKey || 'attractions_detail'}__${lang}`,
          params: { lang, slug: targetSlug },
        });

        return resolved?.href ? `${origin}${resolved.href}` : '';
      } catch (e) {
        return '';
      }
    },
  },

  page() {
    const entity = this.seoEntity || {};

    if (!entity._id && !entity.id && !entity.isStatic) {
      return {};
    }

    const lang = this.currentLanguage;
    const seo = entity.seo || {};
    const content = entity.content || {};
    // const slugs = entity.slugs || entity.urlSlug || {}

    const title =
      seo.title?.[lang] ||
      seo.ogTitle?.[lang] ||
      seo.h1Title?.[lang] ||
      content?.[lang]?.title ||
      'Prague CoolPass';

    // Same precedence the ItemList entries use, so an attraction carries one
    // name whether it is read from its own page or from the area list that
    // links to it. Falls back to the meta title only when the entity has no
    // title of its own - static pages without content still get something.
    const entityName = this.seoEntityTitle || title;

    let description =
      seo.description?.[lang] ||
      seo.ogDescription?.[lang] ||
      content?.[lang]?.description ||
      content?.[lang]?.text ||
      '';

    if (description) {
      description = decodeHtmlEntities(description.replace(/<[^>]*>?/gm, ''))
        .replace(/\s+/g, ' ')
        .substring(0, 160)
        .trim();
    }

    const ogTitle = seo.ogTitle?.[lang] || title;
    const ogDescription = seo.ogDescription?.[lang] || description;

    let rawImage = seo.ogImage || '';

    if (!rawImage) {
      if (entity.mainImage?.web_image) {
        rawImage = entity.mainImage.web_image;
      } else if (entity.webimages?.length) {
        rawImage = entity.webimages[0];
      } else if (entity.images?.length) {
        rawImage = entity.images[0];
      }
    }

    let imagePath = '';

    if (rawImage) {
      if (typeof rawImage === 'string') {
        imagePath = rawImage;
      } else if (typeof rawImage === 'object' && rawImage.url) {
        imagePath = rawImage.url;
      }
    }

    const imageUrl = imagePath
      ? imagePath.startsWith('http')
        ? imagePath
        : `${process.env.VUE_APP_STATIC_URL}${imagePath}`
      : `${this.baseUrl}/img/share-default.jpg`;

    // const links = []
    // const routeBase = this.routeBaseName

    // if (routeBase) {
    //   // hreflang
    //   this.languagesList.forEach((l) => {
    //     const code = l.alpha2code
    //     const targetSlug = slugs[code] || slugs.en || entity.slug

    //     try {
    //       const routeName = `${routeBase}__${code}`

    //       const routeParams = {
    //         ...this.$route.params,
    //         lang: code,
    //       }

    //       if (targetSlug) {
    //         routeParams.slug = targetSlug
    //         routeParams.articleUrl = targetSlug
    //       }

    //       const resolved = this.$router.resolve({
    //         name: routeName,
    //         params: routeParams,
    //       })

    //       if (resolved?.href) {
    //         links.push({
    //           rel: 'alternate',
    //           hreflang: code,
    //           href: `${this.baseUrl}${resolved.href}`,
    //         })
    //       }
    //     } catch (e) {}
    //   })

    //   // canonical
    //   try {
    //     const currentSlug = slugs[lang] || slugs.en || entity.slug

    //     const routeParams = {
    //       ...this.$route.params,
    //       lang,
    //     }

    //     if (currentSlug) {
    //       routeParams.slug = currentSlug
    //       routeParams.articleUrl = currentSlug
    //     }

    //     const resolved = this.$router.resolve({
    //       name: this.$route.name,
    //       params: routeParams,
    //     })

    //     if (resolved?.href) {
    //       links.push({
    //         rel: 'canonical',
    //         href: `${this.baseUrl}${resolved.href}`,
    //       })
    //     }
    //   } catch (e) {}
    // }

    const schemaType = seo.markupType || 'WebPage';

    // vue-meta (keyName: 'page', see main.js) treats this whole method as a
    // Vue computed: it re-evaluates when a REACTIVE dependency it reads
    // changes (this.currentLanguage, this.seoEntity, ...), then flushes the
    // result to the DOM ~10ms later (vue-meta's own debounce). window.location
    // is NOT reactive, so reading window.location.href here does not itself
    // trigger a re-evaluation - the value gets captured whenever some OTHER
    // dependency happens to change, which on a client-side route push landed
    // consistently just BEFORE window.location/history actually updated to
    // the new path. Confirmed locally: canonical/og:url/JSON-LD were captured
    // exactly one route behind on every single client-side transition (never
    // catching up, even 3s later - `pilsner-urquell-new-attraction`,
    // /cs -> /de -> /it -> ..., each snapshot naming the PREVIOUS route).
    // this.$route.fullPath is itself the reactive value that triggers this
    // re-evaluation in the first place, so it can never be one step behind by
    // construction. window.location.origin is still fine to read - it never
    // changes across a client-side navigation, only across a real visit
    // (which is exactly the case this needs to adapt to: staging vs
    // production, with no configuration needed). The one thing this origin
    // can be wrong for is prerendering itself, where Puppeteer's address bar
    // says http://127.0.0.1:8181 (the local build server) - handled as a
    // post-processing step in scripts/prerender.js instead of here, because
    // the correct replacement domain for a prerender run depends on which CI
    // job is building (a staging build with the [prod-data] flag fetches
    // content from production but must still claim the staging domain in its
    // output) - a distinction this component has no way to know, and baseUrl
    // (VUE_APP_BASE_URL) does not track either, since that variable follows
    // the data source, not the deploy target.
    const canonicalUrl = this.seoCanonicalUrl;

    const jsonLd = {
      // No '@context' here - this node is a member of the @graph below, which
      // carries the context for every node in it.
      '@type': schemaType,
      // Same string the ItemList entries use, so an attraction referenced from
      // its area's list and its own page node are one node, not two.
      '@id': canonicalUrl,
      // The name of the thing, not the page's meta title.
      name: entityName,
      description,
      url: canonicalUrl,
      image: imageUrl,
    };

    // `inLanguage` is defined on CreativeWork (plus Event and a few others),
    // NOT on Thing - so it is invalid on the Place, TouristAttraction and
    // OnlineStore nodes the CMS markup field produces, which is roughly 2700
    // of the 3429 live pages. An invalid property is ignored rather than
    // fatal, unlike an invalid @type, but there is no reason to state it.
    // The nested CollectionPage pushed for list pages further down is a
    // CreativeWork and keeps it.
    if (CREATIVE_WORK_PAGE_TYPES.includes(schemaType)) {
      jsonLd.inLanguage = lang;
    }

    if (Array.isArray(entity.faqItems) && entity.faqItems.length) {
      jsonLd.mainEntity = entity.faqItems
        .filter((item) => item.question && item.answer)
        .map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        }));
    }

    const isPlace = PLACE_TYPES.includes(schemaType);

    if (entity.address1 && isPlace) {
      jsonLd.address = {
        '@type': 'PostalAddress',
        streetAddress: entity.address1,
        addressLocality: 'Prague',
        addressCountry: 'CZ',
      };
    }

    if (entity.lat && entity.lon && isPlace) {
      jsonLd.geo = {
        '@type': 'GeoCoordinates',
        latitude: entity.lat,
        longitude: entity.lon,
      };
    }

    // Every page ships the same node under the same `@id`. `origin` (not
    // baseUrl) matches what canonical/og:url already use, and prerender
    // rewrites it in snapshots the same way.
    const origin = window.location.origin;
    const coolpassId = coolpassEntityId(origin);

    let coolpassHomeUrl = '';
    try {
      const resolved = this.$router.resolve({
        name: `home__${lang}`,
        params: { lang },
      });
      if (resolved?.href) coolpassHomeUrl = `${origin}${resolved.href}`;
    } catch (e) {
      // No home route for this language - leave `url` off rather than guess.
    }

    // Null for anything that is not an attraction, and for the 7 free-for-all
    // ones. canAcceptBenefit guards the schema.org domain: 4 attractions are
    // typed `WebPage` in the CMS and cannot carry it until that is corrected.
    const coolpassBenefit = buildCoolpassBenefit(entity);
    if (coolpassBenefit && canAcceptBenefit(schemaType)) {
      jsonLd.additionalProperty = [coolpassBenefit];
    }

    if (CREATIVE_WORK_PAGE_TYPES.includes(schemaType)) {
      jsonLd.about = { '@id': coolpassId };

      // Same domain restriction as `about`, so it rides the same guard.
      const hubMentions = this.seoSharedHubMentions;
      if (hubMentions.length) {
        jsonLd.mentions = hubMentions.map((url) => ({ '@id': url }));
      }
    }

    // A page opts in with `seoListAttractions` - exactly what it renders, in
    // render order - plus `seoListDetailKey` / `seoListFallbackType` when the
    // entries are not attractions.
    //
    // Only a page that renders the COMPLETE set it claims may emit a list.
    // Where the visible list is a subset (load-more, filters, a preview slice)
    // the page states its CollectionPage and stops - hence no list on the
    // attractions hub, which renders 30 of ~130.
    const listAttractions = Array.isArray(this.seoListAttractions) ? this.seoListAttractions : [];

    const graph = [jsonLd, buildCoolpassOrganization({ origin, homeUrl: coolpassHomeUrl })];

    // `seoProductFacts` is an opt-in hook, like `seoListAttractions`: a page
    // that displays offers, benefits or a rating contributes them here.
    //
    // The node is DECLARED only when the page can describe the product, and
    // merely REFERENCED by @id everywhere else. Declaring `@type: Product` is
    // a promise: "you must include one of the following properties: review,
    // aggregateRating, offers", and Google adds that "product rich results
    // only support pages that focus on a single product". We used to declare
    // it on all 3429 pages with none of the three, so Search Console reported
    // every one of them as a product that cannot be shown. An @id reference
    // carries no such promise - it still says "this is about that same pass",
    // which is what the shared id was for.
    //
    // Deliberately keyed on the facts rather than on a list of page names: the
    // price table and the rating arrive through shared blocks that the CMS
    // switches on and off per page, so the only trustworthy test is whether
    // this render actually produced something to say.
    const productFacts = this.seoProductFacts || {};
    const describesProduct = ['offers', 'review', 'aggregateRating'].some(
      (key) => productFacts[key]
    );

    if (describesProduct) {
      graph.splice(
        1,
        0,
        buildCoolpassEntity({ origin, homeUrl: coolpassHomeUrl, facts: productFacts })
      );
    }

    // null for a trail shorter than two crumbs, so the home page emits none.
    const breadcrumbList = buildBreadcrumbList(this.seoBreadcrumbTrail);
    if (breadcrumbList) {
      breadcrumbList['@id'] = `${canonicalUrl}#breadcrumb`;
      graph.push(breadcrumbList);

      if (WEB_PAGE_TYPES.includes(schemaType)) {
        jsonLd.breadcrumb = { '@id': breadcrumbList['@id'] };
      }
    }

    if (listAttractions.length) {
      const itemListElement = listAttractions.map((attraction, index) =>
        buildAttractionListItem({
          position: index + 1,
          name: attraction.content?.[lang]?.title || attraction.content?.en?.title || '',
          url: this.seoAttractionUrl(attraction, lang, origin, this.seoListDetailKey),
          type: attraction.seo?.markupType,
          fallbackType: this.seoListFallbackType,
          benefit: buildCoolpassBenefit(attraction),
        })
      );

      // No itemListOrder: entries carry an explicit `position`, and the real
      // order is not ascending or descending by anything nameable.
      const itemList = {
        '@type': 'ItemList',
        numberOfItems: itemListElement.length,
        itemListElement,
      };

      // `mainEntity` is not valid on Place, which is what an area page's node
      // is. Rather than override the CMS type, the listing gets its own
      // CollectionPage node for the same URL. Theme pages already are one.
      if (schemaType === 'CollectionPage' && !jsonLd.mainEntity) {
        jsonLd.mainEntity = itemList;
      } else {
        graph.push({
          '@type': 'CollectionPage',
          '@id': `${canonicalUrl}#collection`,
          url: canonicalUrl,
          name: title,
          inLanguage: lang,
          about: { '@id': coolpassId },
          mainEntity: itemList,
        });
      }
    }

    const finalGraph = withPageNode({
      graph,
      describesProduct,
      canonicalUrl,
      lang,
      productId: coolpassId,
    });

    return {
      title,

      meta: [
        { vmid: 'description', name: 'description', content: description },

        { vmid: 'og:title', property: 'og:title', content: ogTitle },
        {
          vmid: 'og:description',
          property: 'og:description',
          content: ogDescription,
        },
        { vmid: 'og:image', property: 'og:image', content: imageUrl },
        { vmid: 'og:url', property: 'og:url', content: canonicalUrl },
        { vmid: 'og:locale', property: 'og:locale', content: lang },
        { vmid: 'og:type', property: 'og:type', content: 'website' },
      ],

      // link: links,

      script: [
        {
          vmid: 'ldjson',
          type: 'application/ld+json',
          json: {
            '@context': 'https://schema.org',
            '@graph': finalGraph,
          },
        },
      ],
    };
  },
};
